
import API_ENDPOINTS from '../apiConfig';
import Drag from '../Components/Drag';
import React, { useEffect, useState, useContext, useCallback } from 'react';
import axiosInstance from '../Components/axiosInstance';
import { UserContext } from '../Components/UserContext';
import styles from '../styles/Collections.module.css';
import 'antd/dist/reset.css';

import {
  message,
  Button,
  Tabs,
  Input,
  notification,
  Select,
  Spin,
  Card,
  Row,
  Col,
  Table,
  Popconfirm,
  Space
} from 'antd';

export default function Collections({
  collections,
  setCollections,
  uploadedFiles,
  setUploadedFiles,
  currentCollectionName,
  setCurrentCollectionName,
  selectedCollectionKey,
  setSelectedCollectionKey,
  isLoading,
  setIsLoading,
  loading,
  setLoading,
  Option,
  Text,
  chunkSize,
  setChunkSize,
  overlap,
  setOverlap
}) {
  const { uid } = useContext(UserContext);
  const { token } = useContext(UserContext);

  const onChange = (checked) => {
    setLoading(!checked);
    setIsLoading(!checked)
  };

  useEffect(() => {
    handleRefresh(selectedCollectionKey);
  }, [selectedCollectionKey])
  const fetchCollectionsData = useCallback(() => {
    setIsLoading(true);
    axiosInstance(token)
      .get(`${API_ENDPOINTS.collections}?user_id=${uid}`)
      .then((response) => {
        setCollections(response.data.data);
      })
      .catch((error) => {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch collection. Please try again later.',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, uid]);

  useEffect(() => {
    fetchCollectionsData();
  }, [fetchCollectionsData]);

  const createCollection = () => {
    const nsname = prompt('Enter a name for the new collection:');
    if (nsname) {
      setIsLoading(true);
      axiosInstance(token)
        .post(`${API_ENDPOINTS.collections}?user_id=${uid}&collection_name=${nsname}`)
        .then((response) => {
          console.log('Collection created ', response.data);
          fetchCollectionsData();

          if (response.data && response.data.collection_id) {
            const newCollectionKey = response.data.collection_id;
            setSelectedCollectionKey(newCollectionKey);
            console.log('New collection key: ', newCollectionKey, selectedCollectionKey);
          } else {
            console.log('Unexpected response structure:', response.data);
          }
        })
        .catch((error) => {
          console.log(error);
          notification.error({
            message: 'Error',
            description: 'Failed to create collection. Please try again later.',
          });
        })
    }
  };

  const renameCollection = () => {
    const nskey = selectedCollectionKey;
    const label = collections.find((collection) => collection.collection_id === nskey)?.collection_name;
    const newLabel = prompt(`Enter a new name for ${label}:`);
    if (newLabel) {
      axiosInstance(token)
        .patch(`${API_ENDPOINTS.collections}?user_id=${uid}&collection_id=${nskey}&collection_new_name=${newLabel}`)
        .then((response) => {
          console.log(`Collection ${nskey} renamed to ${newLabel}`);
          fetchCollectionsData();
        })
        .catch((error) => {
          console.log(error);
          notification.error({
            message: 'Error',
            description: 'Failed to rename collection. Please try again later.',
          });
        })
    }
  };

  const deleteCollection = () => {
    const nskey = selectedCollectionKey;

    axiosInstance(token)
      .delete(`${API_ENDPOINTS.collections}?user_id=${uid}&collection_id=${nskey}`)
      .then((response) => {
        console.log('Collection deleted ', response.data);
        // Reset selectedCollectionKey to clear the selection
        setSelectedCollectionKey('');
        fetchCollectionsData(); // Fetch updated list of collections
      })
      .catch((error) => {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to delete collection. Please try again later.',
        });
      })
  };

  const pullList = (key) => {
    if (key === undefined || key === '') {
      console.log('Key is undefined. Axios request not made.');
      return;
    }
    setIsLoading(true);
    axiosInstance(token)
      .get(`${API_ENDPOINTS.collectionsUpload}?user_id=${uid}&collection_id=${key}`)
      .then((response) => {
        setUploadedFiles(response.data.data);
        console.log(response.data.data);

        if (response.data.data.length === 0) {
          message.info('This bucket is empty.');
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response) {
          notification.error({
            message: 'Error',
            description: 'Failed to pull list of collection. Please try again later.',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRefresh = (key) => {
    if (!selectedCollectionKey) {
      return;
    }
    const collection = collections.find((collection) => collection.collection_id === key);
    const collectionName = collection ? collection.collection_name : '';
    setCurrentCollectionName(collectionName);
    pullList(key);
  };

  const handleFileDelete = (filename) => {
    const nskey = selectedCollectionKey;
    const encodedFilename = encodeURIComponent(filename.replace(/ /g, '_'));

    axiosInstance(token)
      .delete(`${API_ENDPOINTS.collectionsUpload}/docs?user_id=${uid}&collection_id=${nskey}&file_name=${encodedFilename}`)
      .then((response) => {
        console.log('File has been deleted ', response.data);
        pullList(nskey);

        message.success('File deleted successfully');
      })
      .catch((error) => {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to delete the file. Please try again later.',
        });
      });
  };

  const columns = [
    {
      title: 'Filename',
      dataIndex: 'file_name',
    },
    {
      title: 'File Status',
      dataIndex: 'status',
    },
    {
      title: 'File Link',
      dataIndex: 's3_url',
      render: (text, record) => (
        <button
          onClick={() => window.open(text, '_blank')}
          style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'blue' }}
        >
          Download
        </button>
      ),
    },
    {
      title: 'No. Vectors',
      dataIndex: 'vector_count',
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
    },
    {
      title: 'Remove ?',
      dataIndex: 'delete_placeholder',
      render: (text, record) => (
        <Popconfirm
          title="Are you sure you want to delete this file?"
          okText="Delete"
          okType="danger"
          cancelText="Cancel"
          onConfirm={() => handleFileDelete(record.file_name)}
        >
          <button
            style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'blue' }}
          >
            Delete
          </button>
        </Popconfirm>
      ),
    },
  ];

  const updatedUploadedFiles = uploadedFiles.map((data, index) => ({
    ...data,
    key: index.toString(),
  }));

  const tableTitle = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px' }}>
      <h3>
        {currentCollectionName === ''
          ? 'Please click the button to refresh table details'
          : `Collection Contents: "${currentCollectionName}"`
        }
      </h3>
    </div>
  );

  const tableFooter = () => null;

  const loadSettings = () => {
    axiosInstance(token)
      .get(`${API_ENDPOINTS.collections}?collection_id=${selectedCollectionKey}&user_id=${uid}`)
      .then((response) => {

        const chunk_size = response.data.data.chunk_size;
        const overlap = response.data.data.overlap;
        setChunkSize(chunk_size);
        setOverlap(overlap);
      })
      .catch((error) => {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to fetch collection details. Please try again later.',
        });
      });
  };

  const saveSettings = () => {
    axiosInstance(token)
      .patch(`${API_ENDPOINTS.collectionsIngest}/docs?collection_id=${selectedCollectionKey}&user_id=${uid}&chunk=${chunkSize}&overlap=${overlap}`)
      .then((response) => {
        console.log('Settings updated successfully');
        // You can show a success message or perform any additional actions here
      })
      .catch((error) => {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to update collection settings. Please try again later.',
        });
      });
  };

  const onSearch = (value) => {
    console.log('search:', value);
  };


  return (
    <div className={styles.mainContainer}>
      <div>
        <Row gutter={18} height={'100%'}>
          <Col span={12} height={'100%'} width={'100%'} align={'center'}>
            <Card bordered hoverable >
              <Tabs size="large">
                <Tabs.TabPane tab={<h3>Select Collection</h3>} key="1">
                  <Card
                    actions={[
                      <Button type="primary" onClick={createCollection}> New </Button>,
                      <Button onClick={renameCollection}> Rename </Button>,
                      <Button onClick={() => handleRefresh(selectedCollectionKey)}> Refresh </Button>,
                      <Popconfirm
                        title="Are you sure you want to delete the collection?"
                        okText="Delete"
                        okType="danger"
                        cancelText="Cancel"
                        onConfirm={deleteCollection}
                      >
                        <Button danger > Delete </Button>
                      </Popconfirm>
                    ]}
                  >
                    <Select
                      showSearch
                      onSearch={onSearch}
                      optionFilterProp="children"
                      style={{ width: '85%' }}
                      value={selectedCollectionKey}
                      onChange={(value) => {
                        setSelectedCollectionKey(value);
                      }}
                      align={'left'}
                    >
                      <Option value="">Select Collection</Option>
                      {collections.map((collection) => (
                        <Option key={collection.collection_id} value={collection.collection_id}>
                          {collection.collection_name}
                        </Option>
                      ))}
                    </Select>
                  </Card>

                </Tabs.TabPane>
                <Tabs.TabPane tab={<h3>Collection Settings</h3>} key="2">
                  <Card
                    size='small'
                    align='left'
                    actions={[
                      <Button size='small' onClick={loadSettings}>Load Settings</Button>,
                      <Button size='small' onClick={saveSettings}>Reprocess Vectors</Button>
                    ]}
                  >
                    <Space direction="horizontal" size="large">
                      <Text className={styles.label}>Chunk Size:</Text>
                      <Input
                        style={{ width: '40%' }}
                        value={chunkSize}
                        onChange={(e) => setChunkSize(e.target.value)}
                      />
                      <Text className={styles.label}>Overlap:</Text>
                      <Input
                        style={{ width: '40%' }}
                        value={overlap}
                        onChange={(e) => setOverlap(e.target.value)}
                      />
                    </Space>

                  </Card>
                </Tabs.TabPane>
              </Tabs>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              className="flexible-card"
              bordered
              hoverable
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <Tabs defaultActiveKey="1" onChange={onChange} size={"large"}>
                <Tabs.TabPane tab={<h3>File Upload</h3>} key="1">
                  <div className="drag">
                    <Drag
                      selectedCollectionKey={selectedCollectionKey}
                      setSelectedCollectionKey={setSelectedCollectionKey}
                      handleRefresh={() => handleRefresh(selectedCollectionKey)}
                    />
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>
      </div>
      <div>
        {isLoading ? (
          <Spin size="large" />
        ) : (
          <Row justify="space-around" align="middle">
            <Col span={24} push={0.5}>
              <Card bordered hoverable>
                {uploadedFiles && (
                  <Table
                    dataSource={updatedUploadedFiles}
                    pagination={false}
                    bordered
                    hoverable
                    columns={columns}
                    title={tableTitle}
                    footer={tableFooter}
                    size="small"
                  />
                )}
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}
